/**
 * PURPOSE:
 * Mostrar categorías públicas de Karate y sus llaves MB/RA/RB consumiendo endpoints optimizados de KarateSoft.
 *
 * RESPONSIBILITIES:
 * - Consumir KarateSoft solo para idevent=223 e idsport=10.
 * - Listar categorías públicas con buscador, orden natural y recarga manual.
 * - Cargar el detalle read-only de una categoría seleccionada.
 * - Renderizar llave principal y repechajes con diseño responsive y scroll bidireccional.
 *
 * COLLABORATORS:
 * - src/components/Software/Karate/services/karateCategoryService.js
 * - KarateCategoryList, KarateBracketHeader y KarateBracketSection.
 * - karateBracketUtils.js
 *
 * ROUTE:
 * src/components/Software/Karate/Categorias/CategoriasKarate.jsx
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getPublicKarateBracketCategory,
  getPublicKarateCategoriesForHayllis,
} from "./services/karateCategoryService.js";
import KarateCategoryList from "./components/KarateCategoryList.jsx";
import KarateBracketHeader from "./components/KarateBracketHeader.jsx";
import KarateBracketSection from "./components/KarateBracketSection.jsx";
import {
  filterMatchesByBracket,
  formatLastUpdated,
  getSafeMatches,
  isKarateHayllisAllowed,
  sortCategoriesByNaturalCode,
} from "./utils/karateBracketUtils.js";
import "./styles/karatePublicBrackets.css";

const getSearchText = (category) => {
  return [
    category?.code,
    category?.description,
    category?.categoryType?.description,
    category?.categoryType?.name,
    category?.modality?.description,
    category?.modality?.name,
    category?.tatami?.name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

export default function CategoriasKarate({ idevent, idsport }) {
  const isAllowed = isKarateHayllisAllowed({ idevent, idsport });

  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedCategoryDetail, setSelectedCategoryDetail] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const selectedCategory = selectedCategoryDetail?.category || null;
  const allMatches = useMemo(() => getSafeMatches(selectedCategoryDetail), [selectedCategoryDetail]);

  const mainMatches = useMemo(() => filterMatchesByBracket(allMatches, "MB"), [allMatches]);
  const repechageAMatches = useMemo(() => filterMatchesByBracket(allMatches, "RA"), [allMatches]);
  const repechageBMatches = useMemo(() => filterMatchesByBracket(allMatches, "RB"), [allMatches]);

  const orderedCategories = useMemo(() => {
    return sortCategoriesByNaturalCode(categories);
  }, [categories]);

  const filteredCategories = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return orderedCategories;

    return orderedCategories.filter((category) => getSearchText(category).includes(query));
  }, [orderedCategories, searchTerm]);

  const loadCategories = useCallback(async () => {
    if (!isAllowed) return;

    try {
      setLoadingCategories(true);
      setError("");
      const data = await getPublicKarateCategoriesForHayllis();
      setCategories(Array.isArray(data) ? data : []);
      setLastUpdatedAt(new Date());
    } catch (requestError) {
      console.error("Error cargando categorías públicas de Karate", requestError);
      setError("No se pudieron cargar las categorías de Karate. Intenta recargar nuevamente.");
    } finally {
      setLoadingCategories(false);
    }
  }, [isAllowed]);

  const loadCategoryDetail = useCallback(async (categoryId) => {
    if (!isAllowed || !categoryId) return;

    try {
      setLoadingDetail(true);
      setError("");
      const data = await getPublicKarateBracketCategory(categoryId);
      setSelectedCategoryDetail(data || null);
      setSelectedCategoryId(categoryId);
      setLastUpdatedAt(new Date());
    } catch (requestError) {
      console.error("Error cargando llave pública de Karate", requestError);
      setError("No se pudo cargar la llave de esta categoría. Intenta recargar nuevamente.");
    } finally {
      setLoadingDetail(false);
    }
  }, [isAllowed]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleSelectCategory = (category) => {
    if (!category?.id) return;
    loadCategoryDetail(category.id);
  };

  const handleRefresh = () => {
    if (selectedCategoryId) {
      loadCategoryDetail(selectedCategoryId);
      return;
    }

    loadCategories();
  };

  const handleBackToList = () => {
    setSelectedCategoryId(null);
    setSelectedCategoryDetail(null);
    setError("");
  };

  if (!isAllowed) {
    return null;
  }

  if (selectedCategoryId) {
    return (
      <div className="karate-public-root mx-auto w-full min-w-0 max-w-full space-y-4 overflow-hidden px-0 sm:max-w-[1400px]">
        <KarateBracketHeader
          category={selectedCategory}
          lastUpdatedAt={lastUpdatedAt}
          loading={loadingDetail}
          onBack={handleBackToList}
          onRefresh={handleRefresh}
        />

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
            {error}
          </div>
        )}

        {loadingDetail && !selectedCategoryDetail ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
            <p className="text-base font-black text-slate-800">Cargando llave...</p>
            <p className="mt-2 text-sm font-semibold text-slate-500">Estamos preparando la llave principal y repechajes.</p>
          </div>
        ) : (
          <div className="w-full min-w-0 max-w-full space-y-4 overflow-hidden">
            <KarateBracketSection
              title="Main Bracket"
              subtitle="Llave principal de la categoría"
              matches={mainMatches}
              accent="blue"
            />
            <KarateBracketSection
              title="Repechaje A"
              subtitle="Repechaje del lado A"
              matches={repechageAMatches}
              accent="red"
            />
            <KarateBracketSection
              title="Repechaje B"
              subtitle="Repechaje del lado B"
              matches={repechageBMatches}
              accent="amber"
            />

            {!mainMatches.length && !repechageAMatches.length && !repechageBMatches.length && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center shadow-sm">
                <p className="text-base font-black text-slate-800">Esta categoría aún no tiene llaves generadas</p>
                <p className="mt-2 text-sm font-semibold text-slate-500">
                  Cuando se generen los brackets en KarateSoft, aparecerán aquí al recargar.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="karate-public-root mx-auto w-full min-w-0 max-w-full space-y-4 overflow-hidden sm:max-w-[1180px]">
      <div className="rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-sm sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <span className="inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-blue-700 ring-1 ring-blue-100">
              Karate · Hayllis
            </span>
            <h2 className="mt-2 text-xl font-black text-slate-900 sm:text-2xl">Categorías</h2>
            <p className="mt-1.5 max-w-2xl text-xs font-semibold leading-relaxed text-slate-500 sm:text-sm">
              Selecciona una categoría para ver la llave principal, repechajes y avance actualizado.
            </p>
          </div>

          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center lg:flex-col lg:items-end">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loadingCategories}
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-3.5 py-2.5 text-xs font-black text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:text-sm"
            >
              {loadingCategories ? "Actualizando..." : "Recargar"}
            </button>
            <p className="text-[11px] font-bold text-slate-400 sm:text-right">
              Última actualización: {formatLastUpdated(lastUpdatedAt)}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <label className="mb-1.5 block text-[11px] font-black uppercase tracking-wide text-slate-500">
            Buscar categoría
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Buscar por A1, B2, nombre, modalidad o tatami..."
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm font-bold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
          {error}
        </div>
      )}

      {loadingCategories && !categories.length ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <p className="text-base font-black text-slate-800">Cargando categorías...</p>
          <p className="mt-2 text-sm font-semibold text-slate-500">Consultando datos públicos de KarateSoft.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-xs font-bold text-slate-500 sm:text-sm">
            <span>{filteredCategories.length} categorías encontradas</span>
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="font-black text-blue-700 hover:underline"
              >
                Limpiar búsqueda
              </button>
            )}
          </div>
          <KarateCategoryList
            categories={filteredCategories}
            onSelect={handleSelectCategory}
          />
        </>
      )}
    </div>
  );
}
